// // src/components/page/ar-space/CoinCanvas.tsx
// 'use client';

// import type React from 'react';
// import Image from 'next/image';
// import Link from 'next/link';
// import type { CSSProperties } from 'react';
// import { useEffect, useMemo, useRef, useState } from 'react';
// import style from './CoinCanvas.module.scss';
// import Coin from '@/components/ui/coin/Coin';
// import Avatar from '@/components/ui/avatar/Avatar';

// // data
// import type { ArSpaceUserArticle } from '@/types/arSpace';
// import ArticleDetailModal from '../article/ArticleDetailModal';

// // === 常數 ===
// const MAX_COIN_COUNT = 25; // 最多顯示顆數

// // 視覺上的籌碼大小 & 間距
// const COIN_RADIUS = 40;
// const COIN_PADDING = 40;

// // 安全半徑稍微放大一點，避免視覺上「貼邊」
// const SAFE_RADIUS_SCALE = 1.2;

// // ✅「絕對不能碰到」的最小間距（中心距離會 ≥ 2*radius + MIN_GAP）
// const MIN_GAP = 50;

// // ⭐ 不同高度模式下最多顯示幾列
// const MAX_ROWS_COMPACT = 1; // 60vh（compact）最高行數
// const MAX_ROWS_TALL = 1; // 100vh（full/fullscreen）最高行數

// // ⭐ 幾顆以內算「小雲團 → 優先置中畫面」
// const SMALL_CLUSTER_THRESHOLD = 10;

// const CTA_OFFSET_AFTER_20TH = 50;
// const CTA_EXPAND_VIEWPORT_RATIO = 0.5;

// // 滾動造成的背景偏移最大值（越小越細膩）
// const SCROLL_PARALLAX_MAX = 15;

// type CloudCoin = {
//   id: string; // 文章用的 id：`${table}_${table_id}`
//   text: string;
//   avatarSrc?: string; // 頭像：kol_u_photo / kol_photo / store_photo
//   emojiPic: string;
//   emojiName: string;
//   ariaLabel: string; // 給按鈕 aria-label
//   x: number;
//   y: number;
// };

// /**
//  * 高度模式：
//  * - compact   → 手機 60dvh - 51px、桌機 60vh - 71px
//  * - full      → 手機 100dvh - 51px、桌機 100vh - 71px
//  * - fullscreen→ 手機/桌機 100vh（不扣 nav）
//  */
// export type CoinCanvasHeightMode = 'compact' | 'full' | 'fullscreen';

// type CoinCanvasProps = {
//   /** 捲動造成的視差 offset（-SCROLL_PARALLAX_MAX ~ +SCROLL_PARALLAX_MAX）往上回報 */
//   onScrollParallaxChange?: (offset: number) => void;
//   /** CTA / AR打卡入住 被點擊時 */
//   onClickArScan?: () => void;
//   /** 高度模式（預設 compact） */
//   heightMode?: CoinCanvasHeightMode;
//   /** AR 空間底下的文章列表（from arSpace.article_data） */
//   articles?: ArSpaceUserArticle[];
//   /** 進文章前要做的事（記錄捲動位置等等） */
//   onBeforeEnterPost?: () => void;
//   /** CTA「寫文入住」按鈕的連結（沒傳就不顯示） */
//   addArticleHref?: string;
//   /** 外部傳入：切換空間時顯示 loading 動畫 */
//   loading?: boolean;
// };

// /** 允許自訂 CSS 變數 */
// type CoinCanvasWrapperStyle = CSSProperties & {
//   '--coinCanvasHeightScale'?: number;
//   '--coinCanvasOffsetMobile'?: number;
//   '--coinCanvasOffsetDesktop'?: number;
// };

// type CreateCloudCoinsOptions = {
//   canvasWidth: number;
//   canvasHeight: number;
//   /** 擺放區域的 X/Y 範圍（在整個畫布中的一塊「雲團區」） */
//   boxXMin: number;
//   boxXMax: number;
//   boxYMin: number;
//   boxYMax: number;
//   /** 是否加重往垂直中心的 bias（手機 true，桌機 false） */
//   biasToCenterY: boolean;
// };

// function buildCloudCoinMeta(a: ArSpaceUserArticle): Omit<CloudCoin, 'x' | 'y'> {
//   const id = `${a.table}_${a.table_id}`;
//   const text = a.depiction?.trim() || '寫下你的入住故事吧';
//   const avatarSrc = a.kol_u_s_photo || a.kol_small_photo || a.store_photo || undefined;
//   const emojiPic = a.emoji_photo || '';
//   const emojiName = a.emoji_name || '';

//   const isStoreArticle = !!a.store_id && !!a.store_name;
//   const ownerName = isStoreArticle ? a.store_name || '店家' : a.kol_name || '作者';
//   const ariaLabel = `開啟 ${ownerName} 的文章`;

//   return { id, text, avatarSrc, emojiPic, emojiName, ariaLabel };
// }

// /**
//  * 雲狀＋排斥力排版：
//  * 1. 先在 boxXMin~boxXMax / boxYMin~boxYMax 隨機撒點
//  *    - X：偏「靠左」的亂數分佈（避免有顆跑很右邊）
//  *    - Y：可選「往中間集中」的 bias
//  * 2. 跑幾輪「兩兩互相排斥」把距離拉開，避免太擠
//  * 3. 壓縮過大的水平縫隙，避免有一顆被推太遠
//  * 4. 加一點 jitter 打散整齊感
//  * 5. 最後再跑「硬碰撞修正」，數學上盡量保證彼此距離 ≥ 2*radius + MIN_GAP
//  */
// function createCloudCoins(
//   articles: ArSpaceUserArticle[],
//   options: CreateCloudCoinsOptions
// ): CloudCoin[] {
//   const { canvasWidth, canvasHeight, boxXMin, boxXMax, boxYMin, boxYMax, biasToCenterY } = options;

//   if (!articles.length || canvasWidth <= 0 || canvasHeight <= 0) return [];

//   const count = Math.min(articles.length, MAX_COIN_COUNT);
//   const coins: CloudCoin[] = [];

//   const safeRadius = COIN_RADIUS * SAFE_RADIUS_SCALE;

//   // 「希望的平均間距」：用來估算雲團寬度 & 排斥力
//   const spacing = safeRadius * 3 + COIN_PADDING;
//   const spacingSq = spacing * spacing;

//   // 「絕對不能低於的距離」：保證不碰撞用
//   const minCollisionDist = safeRadius * 2 + MIN_GAP;
//   const minCollisionDistSq = minCollisionDist * minCollisionDist;

//   const boxWidth = Math.max(10, boxXMax - boxXMin);
//   const boxHeight = Math.max(10, boxYMax - boxYMin);

//   // ==== Step 1：撒初始點（X 偏左、Y 可偏中） ====
//   for (let i = 0; i < count; i += 1) {
//     const meta = buildCloudCoinMeta(articles[i]);

//     // X：偏左（0 ~ 1 的機率分布，靠 0 的機率較高）
//     let rx = Math.random();
//     rx = Math.pow(rx, 1.4); // 指數 > 1 → 偏向 0，越靠左

//     // Y：基礎亂數
//     let ry = Math.random();
//     // 如果希望垂直偏向中間，用兩次亂數平均
//     if (biasToCenterY) {
//       ry = (Math.random() + Math.random()) / 2;
//     }

//     let x = boxXMin + rx * boxWidth;
//     let y = boxYMin + ry * boxHeight;

//     // 邊界保護（至少留一個 safeRadius 的距離）
//     x = Math.min(boxXMax - safeRadius, Math.max(boxXMin + safeRadius, x));
//     y = Math.min(boxYMax - safeRadius, Math.max(boxYMin + safeRadius, y));

//     coins.push({ ...meta, x, y });
//   }

//   // ==== Step 2：排斥力迭代（往 spacing 拉開） ====
//   const iterations = 14;
//   const relaxFactor = 0.5; // 0~1：越小越保守、越大移動越多

//   for (let iter = 0; iter < iterations; iter += 1) {
//     let moved = false;

//     for (let i = 0; i < coins.length; i += 1) {
//       for (let j = i + 1; j < coins.length; j += 1) {
//         const ci = coins[i];
//         const cj = coins[j];

//         let dx = cj.x - ci.x;
//         let dy = cj.y - ci.y;

//         let distSq = dx * dx + dy * dy;

//         // 完全同一點的 extreme case → 給一個小亂數方向
//         if (distSq === 0) {
//           dx = (Math.random() - 0.5) * 0.01;
//           dy = (Math.random() - 0.5) * 0.01;
//           distSq = dx * dx + dy * dy;
//         }

//         // 距離已經 >= spacing，就不處理
//         if (distSq >= spacingSq) continue;

//         const dist = Math.sqrt(distSq);
//         const overlap = (spacing - dist) / 2; // 兩邊各退一半
//         if (overlap <= 0) continue;

//         const nx = dx / dist;
//         const ny = dy / dist;

//         ci.x -= nx * overlap * relaxFactor;
//         ci.y -= ny * overlap * relaxFactor;
//         cj.x += nx * overlap * relaxFactor;
//         cj.y += ny * overlap * relaxFactor;
//         moved = true;
//       }
//     }

//     // 每輪之後再把點拉回邊界內，避免被推到外面
//     for (const c of coins) {
//       c.x = Math.min(boxXMax - safeRadius, Math.max(boxXMin + safeRadius, c.x));
//       c.y = Math.min(boxYMax - safeRadius, Math.max(boxYMin + safeRadius, c.y));
//     }

//     if (!moved) break;
//   }

//   // ==== Step 3：壓縮過大的水平縫隙 → 避免有一顆在很遠的右邊 ====
//   if (coins.length > 2) {
//     const indices = coins.map((_, i) => i).sort((a, b) => coins[a].x - coins[b].x);
//     const maxGapFactor = 2.2; // 允許的最大 gap ≈ 2.2 倍 spacing

//     for (let k = 1; k < indices.length; k += 1) {
//       const left = coins[indices[k - 1]];
//       const right = coins[indices[k]];
//       const gap = right.x - left.x;

//       if (gap > maxGapFactor * spacing) {
//         // 把右邊這顆往左拉近一點（但仍維持 >= spacing 左右）
//         const targetGap = (maxGapFactor * spacing + spacing) / 2; // 介於 [spacing, maxGap]
//         const shift = gap - targetGap;

//         right.x = Math.max(right.x - shift, left.x + spacing);
//       }
//     }
//   }

//   // ==== Step 4：加一點 jitter，打散太整齊的格子感 ====
//   const jitterMax = Math.min(COIN_PADDING * 0.8, 50); // 大約 20~30px 左右
//   if (jitterMax > 0) {
//     for (const c of coins) {
//       const jx = (Math.random() * 2 - 1) * jitterMax;
//       const jy = (Math.random() * 2 - 1) * jitterMax;

//       let nx = c.x + jx;
//       let ny = c.y + jy;

//       // 邊界保護
//       nx = Math.min(boxXMax - safeRadius, Math.max(boxXMin + safeRadius, nx));
//       ny = Math.min(boxYMax - safeRadius, Math.max(boxYMin + safeRadius, ny));

//       c.x = nx;
//       c.y = ny;
//     }
//   }

//   // ==== Step 5：硬碰撞修正 → 儘量保證距離 ≥ minCollisionDist ====
//   const collisionIterations = 18;

//   for (let iter = 0; iter < collisionIterations; iter += 1) {
//     let anyCollision = false;

//     for (let i = 0; i < coins.length; i += 1) {
//       for (let j = i + 1; j < coins.length; j += 1) {
//         const ci = coins[i];
//         const cj = coins[j];

//         let dx = cj.x - ci.x;
//         let dy = cj.y - ci.y;

//         let distSq = dx * dx + dy * dy;
//         if (distSq === 0) {
//           dx = (Math.random() - 0.5) * 0.01;
//           dy = (Math.random() - 0.5) * 0.01;
//           distSq = dx * dx + dy * dy;
//         }

//         if (distSq >= minCollisionDistSq) continue;

//         const dist = Math.sqrt(distSq);
//         const overlap = (minCollisionDist - dist) / 2;
//         if (overlap <= 0) continue;

//         const nx = dx / dist;
//         const ny = dy / dist;

//         // 這裡用比較大的係數，確保快速把「貼在一起」的狀況拉開
//         const pushFactor = 0.9;

//         ci.x -= nx * overlap * pushFactor;
//         ci.y -= ny * overlap * pushFactor;
//         cj.x += nx * overlap * pushFactor;
//         cj.y += ny * overlap * pushFactor;

//         anyCollision = true;
//       }
//     }

//     // 重新 clamp 回邊界內
//     for (const c of coins) {
//       c.x = Math.min(boxXMax - safeRadius, Math.max(boxXMin + safeRadius, c.x));
//       c.y = Math.min(boxYMax - safeRadius, Math.max(boxYMin + safeRadius, c.y));
//     }

//     if (!anyCollision) break;
//   }

//   return coins;
// }

// const CoinCanvas: React.FC<CoinCanvasProps> = ({
//   onScrollParallaxChange,
//   onClickArScan,
//   heightMode = 'compact',
//   articles = [],
//   addArticleHref,
//   onBeforeEnterPost,
//   loading = false,
// }) => {
//   const [articleOpen, setArticleOpen] = useState(false);
//   const [activeArticleId, setActiveArticleId] = useState<string | null>(null);

//   const handleArticleOpen = (id: string) => {
//     if (onBeforeEnterPost) onBeforeEnterPost();
//     setActiveArticleId(id);
//     setArticleOpen(true);
//   };

//   const handleArticleClose = () => {
//     setArticleOpen(false);

//     if (activeArticleId) {
//       const opener = document.querySelector<HTMLElement>(
//         `[data-opener="article-${activeArticleId}"]`
//       );
//       setTimeout(() => opener?.focus?.({ preventScroll: true }), 0);
//     }
//   };

//   const [cloudCoins, setCloudCoins] = useState<CloudCoin[]>([]);
//   const [canvasWidth, setCanvasWidth] = useState<number>(800);

//   const coinCanvasRef = useRef<HTMLDivElement | null>(null);
//   const coinScrollRef = useRef<HTMLDivElement | null>(null);

//   const [canvasHeight, setCanvasHeight] = useState<number | null>(null);

//   // CTA 狀態
//   const [showEndCta, setShowEndCta] = useState(false);
//   const [expandEndCta, setExpandEndCta] = useState(false);

//   // 依 heightMode 設定 CSS 變數
//   const wrapperStyle: CoinCanvasWrapperStyle = useMemo(() => {
//     let heightScale = 60; // 60 / 100
//     let offsetMobile = 51;
//     let offsetDesktop = 71;

//     switch (heightMode) {
//       case 'full':
//         heightScale = 100;
//       // fallthrough
//       case 'compact':
//         // compact / full 共用 offset
//         offsetMobile = 51;
//         offsetDesktop = 71;
//         break;
//       case 'fullscreen':
//         heightScale = 100;
//         offsetMobile = 0;
//         offsetDesktop = 0;
//         break;
//       default:
//         break;
//     }

//     return {
//       '--coinCanvasHeightScale': heightScale,
//       '--coinCanvasOffsetMobile': offsetMobile,
//       '--coinCanvasOffsetDesktop': offsetDesktop,
//       position: 'relative', // 為了讓 loading overlay 能絕對定位
//     };
//   }, [heightMode]);

//   // 量 canvas 高度
//   useEffect(() => {
//     if (typeof window === 'undefined') return;

//     const updateHeight = () => {
//       if (coinCanvasRef.current) {
//         const h = coinCanvasRef.current.clientHeight;
//         if (h > 0) setCanvasHeight(h);
//       }
//     };

//     updateHeight();
//     window.addEventListener('resize', updateHeight);
//     return () => window.removeEventListener('resize', updateHeight);
//   }, []);

//   // 依高度 + articles 重新排籌碼，並調整畫布寬度
//   useEffect(() => {
//     if (!canvasHeight) return;

//     const totalCount = Math.min(articles.length, MAX_COIN_COUNT);
//     const safeRadius = COIN_RADIUS * SAFE_RADIUS_SCALE;
//     const baseStep = safeRadius * 2 + COIN_PADDING; // 估算 spacing

//     const viewportWidth = coinScrollRef.current?.clientWidth || 320;
//     const isMobile = viewportWidth <= 768;
//     const isTallHeight = heightMode === 'full' || heightMode === 'fullscreen';

//     // 可用的垂直空間（扣掉上下各一個 safeRadius）
//     const usableHeight = Math.max(canvasHeight - safeRadius * 2, baseStep);
//     const rowsCap = Math.max(1, Math.floor(usableHeight / baseStep));

//     // 依照「總顆數」算一個基礎建議行數
//     let rowsBase: number;
//     if (totalCount <= 3) rowsBase = 1;
//     else if (totalCount <= 5) rowsBase = 2;
//     else rowsBase = 3;

//     // ⭐ 依高度模式，限制最多幾行
//     const maxRowsForMode = isTallHeight ? MAX_ROWS_TALL : MAX_ROWS_COMPACT;

//     // 真正拿來用的行數
//     const rows = Math.min(rowsBase, rowsCap, maxRowsForMode);

//     // 用 rows 估算這坨雲大概要多寬
//     const colsNeeded = Math.max(1, Math.ceil(totalCount / rows));
//     const clusterWidthEst = colsNeeded * baseStep;

//     // ⭐ 幾顆以內算「小雲團 → 優先置中畫面」
//     const isSmallCluster = totalCount <= SMALL_CLUSTER_THRESHOLD;

//     let finalCanvasWidth = viewportWidth;
//     let boxXMin: number;
//     let boxXMax: number;

//     if (clusterWidthEst + safeRadius * 2 <= viewportWidth) {
//       // 雲團塞得進 viewport → 不需要水平捲軸
//       finalCanvasWidth = viewportWidth;

//       if (isSmallCluster) {
//         // 小雲團 → 置中
//         const centerX = viewportWidth / 2;
//         const halfCluster = clusterWidthEst / 2;

//         boxXMin = Math.max(safeRadius, centerX - halfCluster);
//         boxXMax = Math.min(viewportWidth - safeRadius, centerX + halfCluster);
//       } else {
//         // 其他 → 靠左一點
//         boxXMin = safeRadius;
//         boxXMax = boxXMin + clusterWidthEst;
//       }
//     } else {
//       // 需要水平捲軸 → 畫布寬度 = 雲團估算寬度 + 左右 margin
//       finalCanvasWidth = clusterWidthEst + safeRadius * 2;
//       boxXMin = safeRadius;
//       boxXMax = finalCanvasWidth - safeRadius;
//     }

//     // 垂直方向：頂部 & 底部各留一點 margin，讓雲團集中在中間帶
//     const topMargin = safeRadius;
//     const bottomMargin = safeRadius;
//     const boxYMin = topMargin;
//     const boxYMax = canvasHeight - bottomMargin;

//     const coins = createCloudCoins(articles, {
//       canvasWidth: finalCanvasWidth,
//       canvasHeight,
//       boxXMin,
//       boxXMax,
//       boxYMin,
//       boxYMax,
//       biasToCenterY: isMobile, // 手機版垂直方向集中在中間
//     });

//     setCloudCoins(coins);
//     setCanvasWidth(finalCanvasWidth);
//   }, [canvasHeight, articles, heightMode]);

//   // ✨ 當 articles 改變時：捲軸回到最左邊 + 視差歸零
//   useEffect(() => {
//     if (coinScrollRef.current) {
//       coinScrollRef.current.scrollLeft = 0;
//     }
//     if (onScrollParallaxChange) {
//       onScrollParallaxChange(0);
//     }
//   }, [articles, onScrollParallaxChange]);

//   // 第 20 顆（或最後一顆） 的 X 座標
//   const endPanelTargetX = useMemo(() => {
//     if (!cloudCoins.length) return null;
//     const sorted = [...cloudCoins].sort((a, b) => a.x - b.x);
//     const targetIndex = Math.min(MAX_COIN_COUNT - 1, sorted.length - 1);
//     return sorted[targetIndex]?.x ?? null;
//   }, [cloudCoins]);

//   // CTA 起始 X（接在第 20 顆後面）→ 只拿來控制「何時出現」，不再拿來定位
//   const ctaStartX = useMemo(() => {
//     if (endPanelTargetX == null) return null;
//     return endPanelTargetX + CTA_OFFSET_AFTER_20TH;
//   }, [endPanelTargetX]);

//   // 滑鼠滾輪 → 改成水平捲動
//   const handleCoinWheel = (e: React.WheelEvent<HTMLDivElement>) => {
//     const el = e.currentTarget;
//     if (!el) return;
//     if (el.scrollWidth <= el.clientWidth) return;

//     // 垂直滾動 → 轉成水平滾
//     if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
//       el.scrollLeft += e.deltaY;
//       e.preventDefault();
//     }
//   };

//   // scroll → 控制 CTA 顯示 & 視差（不再改 scrollLeft）
//   const handleCoinScroll = (e: React.UIEvent<HTMLDivElement>) => {
//     const el = e.currentTarget;
//     const { scrollLeft, clientWidth, scrollWidth } = el;

//     const maxScroll = scrollWidth - clientWidth;

//     if (ctaStartX == null) {
//       setShowEndCta(false);
//       setExpandEndCta(false);
//     } else {
//       const rightEdge = scrollLeft + clientWidth;

//       const shouldShow = rightEdge >= ctaStartX;
//       setShowEndCta(shouldShow);

//       const expandTriggerX = ctaStartX + clientWidth * CTA_EXPAND_VIEWPORT_RATIO;
//       const shouldExpand = rightEdge >= expandTriggerX;
//       setExpandEndCta(shouldExpand);
//     }

//     // scroll 視差往外丟
//     if (onScrollParallaxChange) {
//       if (maxScroll > 0) {
//         const progress = scrollLeft / maxScroll; // 0 ~ 1
//         const offset = (progress - 0.5) * 2 * SCROLL_PARALLAX_MAX; // -MAX ~ +MAX
//         onScrollParallaxChange(offset);
//       } else {
//         onScrollParallaxChange(0);
//       }
//     }
//   };

//   // 卸載時把 scroll 視差歸零
//   useEffect(
//     () => () => {
//       if (onScrollParallaxChange) onScrollParallaxChange(0);
//     },
//     [onScrollParallaxChange]
//   );

//   return (
//     <>
//       <div
//         className={style.coinCanvasWrapper}
//         style={wrapperStyle}
//         ref={coinScrollRef}
//         onWheel={handleCoinWheel}
//         onScroll={handleCoinScroll}
//       >
//         {/* ✨ 切換空間時的小 loading 動畫（蓋在籌碼上方） */}
//         {loading && (
//           <div
//             className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
//             style={{ zIndex: 5, pointerEvents: 'none' }}
//           >
//             <div className="spinner-border text-light" role="status">
//               <span className="visually-hidden">Loading...</span>
//             </div>
//           </div>
//         )}

//         {/* 真正籌碼畫布 */}
//         <div className={style.coinCanvas} ref={coinCanvasRef} style={{ width: canvasWidth }}>
//           {cloudCoins.map((coin) => (
//             <div
//               key={coin.id}
//               className={style.coinItem}
//               style={{ left: `${coin.x}px`, top: `${coin.y}px` }}
//             >
//               <button
//                 type="button"
//                 data-opener={`article-${coin.id}`}
//                 onClick={() => handleArticleOpen(coin.id)}
//                 className="fs-sm-1"
//                 aria-label={coin.ariaLabel}
//               >
//                 <Coin scale={0.8} color="green">
//                   {coin.emojiPic && (
//                     <div className={style.emojiPic}>
//                       <Image
//                         src={coin.emojiPic}
//                         alt={coin.emojiName || 'emoji'}
//                         width={25}
//                         height={25}
//                         className="ms-1 align-text-bottom"
//                       />
//                     </div>
//                   )}
//                   <Avatar src={coin.avatarSrc} />
//                 </Coin>
//                 <span className="text-center fs-sm-2 text-shadow-1">{coin.text}</span>
//               </button>
//             </div>
//           ))}

//           {/* CTA 區：二十顆之後「才開始出現」，位置永遠靠右側 */}
//           {ctaStartX != null && (
//             <div
//               className={`${style.coinEndPanel} ${
//                 showEndCta ? style.coinEndPanelVisible : ''
//               } ${expandEndCta ? style.coinEndPanelExpanded : ''}`}
//             >
//               <div className={style.coinEndPanelInner}>
//                 <p className="mb-3 text-white fs-sm-1 text-center">
//                   這麼多入住回憶，輪到你來寫一篇了！
//                 </p>
//                 <div className={`d-flex flex-column flex-md-row gap-2 w-100 ${style.btnGoAR}`}>
//                   {addArticleHref && (
//                     <Link
//                       href={addArticleHref}
//                       target="_self"
//                       className="btn btn-light btn-sm rounded-pill flex-fill px-3 mb-2 mb-md-0"
//                     >
//                       寫文入住
//                     </Link>
//                   )}
//                   <button
//                     type="button"
//                     className="btn btn-brand btn-sm rounded-pill flex-fill px-3 bg-brand text-white border-0"
//                     onClick={onClickArScan}
//                   >
//                     AR入住
//                   </button>
//                 </div>
//               </div>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* 文章 modal */}
//       {activeArticleId && (
//         <ArticleDetailModal
//           open={articleOpen}
//           articleId={activeArticleId}
//           onClose={handleArticleClose}
//         />
//       )}
//     </>
//   );
// };

// export default CoinCanvas;

// src/components/page/ar-space/CoinCanvas.tsx
'use client';

import type React from 'react';
import Image from 'next/image';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import style from './CoinCanvas.module.scss';
import Coin from '@/components/ui/coin/Coin';
import Avatar from '@/components/ui/avatar/Avatar';

// data
import type { ArSpaceUserArticle } from '@/types/arSpace';
import ArticleDetailModal from '../article/ArticleDetailModal';
import { useAppSelector } from '@/store/hook';

// === 常數 ===
const MAX_COIN_COUNT = 25; // 最多顯示顆數

// 視覺上的籌碼大小 & 間距
const COIN_RADIUS = 40;
const COIN_PADDING = 40;

// 安全半徑稍微放大一點，避免視覺上「貼邊」
const SAFE_RADIUS_SCALE = 1.2;

// ✅「絕對不能碰到」的最小間距（中心距離會 ≥ 2*radius + MIN_GAP）
const MIN_GAP = 50;

// ⭐ 不同高度模式下最多顯示幾列
const MAX_ROWS_COMPACT = 1; // 60vh（compact）最高行數
const MAX_ROWS_TALL = 1; // 100vh（full/fullscreen）最高行數

// ⭐ 幾顆以內算「小雲團 → 優先置中畫面」
const SMALL_CLUSTER_THRESHOLD = 10;

const CTA_OFFSET_AFTER_20TH = 50;
const CTA_EXPAND_VIEWPORT_RATIO = 0.5;

// 滾動造成的背景偏移最大值（越小越細膩）
const SCROLL_PARALLAX_MAX = 15;

type CloudCoin = {
  id: string; // 文章用的 id：`${table}_${table_id}`
  text: string;
  avatarSrc?: string;
  emojiPic: string;
  emojiName: string;
  ariaLabel: string;
  x: number;
  y: number;
};

/**
 * 高度模式：
 * - compact   → 手機 60dvh - 51px、桌機 60vh - 71px
 * - full      → 手機 100dvh - 51px、桌機 100vh - 71px
 * - fullscreen→ 手機/桌機 100vh（不扣 nav）
 */
export type CoinCanvasHeightMode = 'compact' | 'full' | 'fullscreen';

type CoinCanvasProps = {
  /** 捲動造成的視差 offset（-SCROLL_PARALLAX_MAX ~ +SCROLL_PARALLAX_MAX）往上回報 */
  onScrollParallaxChange?: (offset: number) => void;
  /** CTA / AR打卡入住 被點擊時 */
  onClickArScan?: () => void;
  /** 高度模式（預設 compact） */
  heightMode?: CoinCanvasHeightMode;
  /** AR 空間底下的文章列表（from arSpace.article_data） */
  articles?: ArSpaceUserArticle[];
  /** 進文章前要做的事（記錄捲動位置等等） */
  onBeforeEnterPost?: () => void;
  /** CTA「寫文入住」按鈕的連結（沒傳就不顯示） */
  addArticleHref?: string;
  /** 外部傳入：切換空間時顯示 loading 動畫 */
  loading?: boolean;
};

/** 允許自訂 CSS 變數 */
type CoinCanvasWrapperStyle = CSSProperties & {
  '--coinCanvasHeightScale'?: number;
  '--coinCanvasOffsetMobile'?: number;
  '--coinCanvasOffsetDesktop'?: number;
};

type CreateCloudCoinsOptions = {
  canvasWidth: number;
  canvasHeight: number;
  boxXMin: number;
  boxXMax: number;
  boxYMin: number;
  boxYMax: number;
  biasToCenterY: boolean;
};

function buildCloudCoinMeta(a: ArSpaceUserArticle): Omit<CloudCoin, 'x' | 'y'> {
  const id = `${a.table}_${a.table_id}`;
  const text = a.depiction?.trim() || '寫下你的入住故事吧';

  // ✅ 你提到常用的是 kol_u_s_photo / emoji_photo / emojiName
  const avatarSrc = a.kol_u_s_photo || a.kol_small_photo || a.store_photo || undefined;
  const emojiPic = a.emoji_photo || '';
  const emojiName = a.emoji_name || '';

  const isStoreArticle = !!a.store_id && !!a.store_name;
  const ownerName = isStoreArticle ? a.store_name || '店家' : a.kol_name || '作者';
  const ariaLabel = `開啟 ${ownerName} 的文章`;

  return { id, text, avatarSrc, emojiPic, emojiName, ariaLabel };
}

function createCloudCoins(
  articles: ArSpaceUserArticle[],
  options: CreateCloudCoinsOptions
): CloudCoin[] {
  const { canvasWidth, canvasHeight, boxXMin, boxXMax, boxYMin, boxYMax, biasToCenterY } = options;

  if (!articles.length || canvasWidth <= 0 || canvasHeight <= 0) return [];

  const count = Math.min(articles.length, MAX_COIN_COUNT);
  const coins: CloudCoin[] = [];

  const safeRadius = COIN_RADIUS * SAFE_RADIUS_SCALE;

  const spacing = safeRadius * 3 + COIN_PADDING;
  const spacingSq = spacing * spacing;

  const minCollisionDist = safeRadius * 2 + MIN_GAP;
  const minCollisionDistSq = minCollisionDist * minCollisionDist;

  const boxWidth = Math.max(10, boxXMax - boxXMin);
  const boxHeight = Math.max(10, boxYMax - boxYMin);

  // Step 1：撒初始點
  for (let i = 0; i < count; i += 1) {
    const meta = buildCloudCoinMeta(articles[i]);

    let rx = Math.random();
    rx = Math.pow(rx, 1.4);

    let ry = Math.random();
    if (biasToCenterY) {
      ry = (Math.random() + Math.random()) / 2;
    }

    let x = boxXMin + rx * boxWidth;
    let y = boxYMin + ry * boxHeight;

    x = Math.min(boxXMax - safeRadius, Math.max(boxXMin + safeRadius, x));
    y = Math.min(boxYMax - safeRadius, Math.max(boxYMin + safeRadius, y));

    coins.push({ ...meta, x, y });
  }

  // Step 2：排斥力迭代
  const iterations = 14;
  const relaxFactor = 0.5;

  for (let iter = 0; iter < iterations; iter += 1) {
    let moved = false;

    for (let i = 0; i < coins.length; i += 1) {
      for (let j = i + 1; j < coins.length; j += 1) {
        const ci = coins[i];
        const cj = coins[j];

        let dx = cj.x - ci.x;
        let dy = cj.y - ci.y;

        let distSq = dx * dx + dy * dy;

        if (distSq === 0) {
          dx = (Math.random() - 0.5) * 0.01;
          dy = (Math.random() - 0.5) * 0.01;
          distSq = dx * dx + dy * dy;
        }

        if (distSq >= spacingSq) continue;

        const dist = Math.sqrt(distSq);
        const overlap = (spacing - dist) / 2;
        if (overlap <= 0) continue;

        const nx = dx / dist;
        const ny = dy / dist;

        ci.x -= nx * overlap * relaxFactor;
        ci.y -= ny * overlap * relaxFactor;
        cj.x += nx * overlap * relaxFactor;
        cj.y += ny * overlap * relaxFactor;
        moved = true;
      }
    }

    for (const c of coins) {
      c.x = Math.min(boxXMax - safeRadius, Math.max(boxXMin + safeRadius, c.x));
      c.y = Math.min(boxYMax - safeRadius, Math.max(boxYMin + safeRadius, c.y));
    }

    if (!moved) break;
  }

  // Step 3：壓縮過大的水平縫隙
  if (coins.length > 2) {
    const indices = coins.map((_, i) => i).sort((a, b) => coins[a].x - coins[b].x);
    const maxGapFactor = 2.2;

    for (let k = 1; k < indices.length; k += 1) {
      const left = coins[indices[k - 1]];
      const right = coins[indices[k]];
      const gap = right.x - left.x;

      if (gap > maxGapFactor * spacing) {
        const targetGap = (maxGapFactor * spacing + spacing) / 2;
        const shift = gap - targetGap;
        right.x = Math.max(right.x - shift, left.x + spacing);
      }
    }
  }

  // Step 4：jitter
  const jitterMax = Math.min(COIN_PADDING * 0.8, 50);
  if (jitterMax > 0) {
    for (const c of coins) {
      const jx = (Math.random() * 2 - 1) * jitterMax;
      const jy = (Math.random() * 2 - 1) * jitterMax;

      let nx = c.x + jx;
      let ny = c.y + jy;

      nx = Math.min(boxXMax - safeRadius, Math.max(boxXMin + safeRadius, nx));
      ny = Math.min(boxYMax - safeRadius, Math.max(boxYMin + safeRadius, ny));

      c.x = nx;
      c.y = ny;
    }
  }

  // Step 5：硬碰撞修正
  const collisionIterations = 18;

  for (let iter = 0; iter < collisionIterations; iter += 1) {
    let anyCollision = false;

    for (let i = 0; i < coins.length; i += 1) {
      for (let j = i + 1; j < coins.length; j += 1) {
        const ci = coins[i];
        const cj = coins[j];

        let dx = cj.x - ci.x;
        let dy = cj.y - ci.y;

        let distSq = dx * dx + dy * dy;
        if (distSq === 0) {
          dx = (Math.random() - 0.5) * 0.01;
          dy = (Math.random() - 0.5) * 0.01;
          distSq = dx * dx + dy * dy;
        }

        if (distSq >= minCollisionDistSq) continue;

        const dist = Math.sqrt(distSq);
        const overlap = (minCollisionDist - dist) / 2;
        if (overlap <= 0) continue;

        const nx = dx / dist;
        const ny = dy / dist;

        const pushFactor = 0.9;

        ci.x -= nx * overlap * pushFactor;
        ci.y -= ny * overlap * pushFactor;
        cj.x += nx * overlap * pushFactor;
        cj.y += ny * overlap * pushFactor;

        anyCollision = true;
      }
    }

    for (const c of coins) {
      c.x = Math.min(boxXMax - safeRadius, Math.max(boxXMin + safeRadius, c.x));
      c.y = Math.min(boxYMax - safeRadius, Math.max(boxYMin + safeRadius, c.y));
    }

    if (!anyCollision) break;
  }

  return coins;
}

const CoinCanvas: React.FC<CoinCanvasProps> = ({
  onScrollParallaxChange,
  onClickArScan,
  heightMode = 'compact',
  articles = [],
  addArticleHref,
  onBeforeEnterPost,
  loading = false,
}) => {
  const router = useRouter();
  const token = useAppSelector((state) => state.auth.token);
  const [articleOpen, setArticleOpen] = useState(false);
  const [activeArticleId, setActiveArticleId] = useState<string | null>(null);

  const handleArticleOpen = useCallback(
    (id: string) => {
      onBeforeEnterPost?.();
      setActiveArticleId(id);
      setArticleOpen(true);
    },
    [onBeforeEnterPost]
  );

  const handleArticleClose = useCallback(() => {
    setArticleOpen(false);

    if (activeArticleId) {
      const opener = document.querySelector<HTMLElement>(
        `[data-opener="article-${activeArticleId}"]`
      );
      setTimeout(() => opener?.focus?.({ preventScroll: true }), 0);
    }
  }, [activeArticleId]);

  // const router = useRouter();
  // const searchParams = useSearchParams();
  // const pathname = usePathname();

  // // ✅ 監聽網址參數：當網址參數改變時，同步 Modal 狀態
  // useEffect(() => {
  //   const id = searchParams.get('articleId');
  //   if (id) {
  //     setActiveArticleId(id);
  //     setArticleOpen(true);
  //   } else {
  //     setArticleOpen(false);
  //     // 不要清空 activeArticleId，這樣關閉動畫才不會報錯，
  //     // 但 handleArticleClose 結束後可以視情況清空
  //   }
  // }, [searchParams]);

  // // ✅ 修改開啟邏輯：推入帶參數的網址
  // const handleArticleOpen = useCallback(
  //   (id: string) => {
  //     onBeforeEnterPost?.();
  //     // 在 App Router，我們直接推網址，useEffect 會處理開啟
  //     router.push(`${pathname}?articleId=${id}`);
  //   },
  //   [onBeforeEnterPost, pathname, router]
  // );

  // // ✅ 修改關閉邏輯：回到原本路徑
  // const handleArticleClose = useCallback(() => {
  //   // 這裡只需要做 router.back() 或 push 回去即可
  //   // useEffect 會監聽到參數消失並關閉 Modal
  //   router.push(pathname);

  //   if (activeArticleId) {
  //     const opener = document.querySelector<HTMLElement>(
  //       `[data-opener="article-${activeArticleId}"]`
  //     );
  //     setTimeout(() => opener?.focus?.({ preventScroll: true }), 0);
  //   }
  // }, [activeArticleId, pathname, router]);

  const [cloudCoins, setCloudCoins] = useState<CloudCoin[]>([]);
  const [canvasWidth, setCanvasWidth] = useState<number>(800);
  const [canvasHeight, setCanvasHeight] = useState<number | null>(null);

  // ✅ ref 一定要打型別（不然很容易變 never）
  const coinCanvasRef = useRef<HTMLDivElement | null>(null);
  const coinScrollRef = useRef<HTMLDivElement | null>(null);

  // CTA 狀態
  const [showEndCta, setShowEndCta] = useState(false);
  const [expandEndCta, setExpandEndCta] = useState(false);

  // 依 heightMode 設定 CSS 變數
  const wrapperStyle: CoinCanvasWrapperStyle = useMemo(() => {
    let heightScale = 60;
    let offsetMobile = 51;
    let offsetDesktop = 71;

    switch (heightMode) {
      case 'full':
        heightScale = 100;
      // fallthrough
      case 'compact':
        offsetMobile = 51;
        offsetDesktop = 71;
        break;
      case 'fullscreen':
        heightScale = 100;
        offsetMobile = 0;
        offsetDesktop = 0;
        break;
      default:
        break;
    }

    return {
      '--coinCanvasHeightScale': heightScale,
      '--coinCanvasOffsetMobile': offsetMobile,
      '--coinCanvasOffsetDesktop': offsetDesktop,
      position: 'relative',
    };
  }, [heightMode]);

  // ✅ 量 canvas 高度：ResizeObserver（更快、更準；resize fallback）
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const el = coinCanvasRef.current;
    if (!el) return;

    const update = () => {
      const h = el.clientHeight;
      if (h > 0) setCanvasHeight((prev) => (prev === h ? prev : h));
    };

    update();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => update());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // 依高度 + articles 重新排籌碼，並調整畫布寬度
  useEffect(() => {
    if (!canvasHeight) return;

    const totalCount = Math.min(articles.length, MAX_COIN_COUNT);
    const safeRadius = COIN_RADIUS * SAFE_RADIUS_SCALE;
    const baseStep = safeRadius * 2 + COIN_PADDING;

    const viewportWidth = coinScrollRef.current?.clientWidth || 320;
    const isMobile = viewportWidth <= 768;
    const isTallHeight = heightMode === 'full' || heightMode === 'fullscreen';

    const usableHeight = Math.max(canvasHeight - safeRadius * 2, baseStep);
    const rowsCap = Math.max(1, Math.floor(usableHeight / baseStep));

    let rowsBase: number;
    if (totalCount <= 3) rowsBase = 1;
    else if (totalCount <= 5) rowsBase = 2;
    else rowsBase = 3;

    const maxRowsForMode = isTallHeight ? MAX_ROWS_TALL : MAX_ROWS_COMPACT;
    const rows = Math.min(rowsBase, rowsCap, maxRowsForMode);

    const colsNeeded = Math.max(1, Math.ceil(totalCount / rows));
    const clusterWidthEst = colsNeeded * baseStep;

    const isSmallCluster = totalCount <= SMALL_CLUSTER_THRESHOLD;

    let finalCanvasWidth = viewportWidth;
    let boxXMin: number;
    let boxXMax: number;

    if (clusterWidthEst + safeRadius * 2 <= viewportWidth) {
      finalCanvasWidth = viewportWidth;

      if (isSmallCluster) {
        const centerX = viewportWidth / 2;
        const halfCluster = clusterWidthEst / 2;

        boxXMin = Math.max(safeRadius, centerX - halfCluster);
        boxXMax = Math.min(viewportWidth - safeRadius, centerX + halfCluster);
      } else {
        boxXMin = safeRadius;
        boxXMax = boxXMin + clusterWidthEst;
      }
    } else {
      finalCanvasWidth = clusterWidthEst + safeRadius * 2;
      boxXMin = safeRadius;
      boxXMax = finalCanvasWidth - safeRadius;
    }

    const topMargin = safeRadius;
    const bottomMargin = safeRadius;
    const boxYMin = topMargin;
    const boxYMax = canvasHeight - bottomMargin;

    const coins = createCloudCoins(articles, {
      canvasWidth: finalCanvasWidth,
      canvasHeight,
      boxXMin,
      boxXMax,
      boxYMin,
      boxYMax,
      biasToCenterY: isMobile,
    });

    setCloudCoins(coins);
    setCanvasWidth((prev) => (prev === finalCanvasWidth ? prev : finalCanvasWidth));
  }, [canvasHeight, articles, heightMode]);

  // 當 articles 改變時：捲軸回到最左邊 + 視差歸零
  useEffect(() => {
    const el = coinScrollRef.current;
    if (el) el.scrollLeft = 0;
    onScrollParallaxChange?.(0);
  }, [articles, onScrollParallaxChange]);

  // 第 20 顆（或最後一顆） 的 X 座標
  const endPanelTargetX = useMemo(() => {
    if (!cloudCoins.length) return null;
    const sorted = [...cloudCoins].sort((a, b) => a.x - b.x);
    const targetIndex = Math.min(MAX_COIN_COUNT - 1, sorted.length - 1);
    return sorted[targetIndex]?.x ?? null;
  }, [cloudCoins]);

  // CTA 起始 X（接在第 20 顆後面）
  const ctaStartX = useMemo(() => {
    if (endPanelTargetX == null) return null;
    return endPanelTargetX + CTA_OFFSET_AFTER_20TH;
  }, [endPanelTargetX]);

  // ✅ 避免 rAF callback 捕捉到舊值：用 ref 存最新 ctaStartX / callback
  const ctaStartXRef = useRef<number | null>(null);
  useEffect(() => {
    ctaStartXRef.current = ctaStartX;
  }, [ctaStartX]);

  const onParallaxRef = useRef<CoinCanvasProps['onScrollParallaxChange']>(onScrollParallaxChange);
  useEffect(() => {
    onParallaxRef.current = onScrollParallaxChange;
  }, [onScrollParallaxChange]);

  // 滑鼠滾輪 → 改成水平捲動
  const handleCoinWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!el) return;
    if (el.scrollWidth <= el.clientWidth) return;

    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  }, []);

  // ✅ scroll → rAF 節流 + 只有變動才 setState（避免頻繁 re-render）
  const scrollRafRef = useRef<number | null>(null);
  const scrollSnapshotRef = useRef({ left: 0, cw: 0, sw: 0 });

  const handleCoinScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    scrollSnapshotRef.current = {
      left: el.scrollLeft,
      cw: el.clientWidth,
      sw: el.scrollWidth,
    };

    if (typeof window === 'undefined') return;
    if (scrollRafRef.current != null) return;

    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;

      const { left: scrollLeft, cw: clientWidth, sw: scrollWidth } = scrollSnapshotRef.current;
      const maxScroll = scrollWidth - clientWidth;

      const startX = ctaStartXRef.current;

      if (startX == null) {
        setShowEndCta((prev) => (prev ? false : prev));
        setExpandEndCta((prev) => (prev ? false : prev));
      } else {
        const rightEdge = scrollLeft + clientWidth;

        const nextShow = rightEdge >= startX;
        const expandTriggerX = startX + clientWidth * CTA_EXPAND_VIEWPORT_RATIO;
        const nextExpand = rightEdge >= expandTriggerX;

        setShowEndCta((prev) => (prev === nextShow ? prev : nextShow));
        setExpandEndCta((prev) => (prev === nextExpand ? prev : nextExpand));
      }

      const cb = onParallaxRef.current;
      if (cb) {
        if (maxScroll > 0) {
          const progress = scrollLeft / maxScroll; // 0~1
          const offset = (progress - 0.5) * 2 * SCROLL_PARALLAX_MAX; // -MAX~+MAX
          cb(offset);
        } else {
          cb(0);
        }
      }
    });
  }, []);

  // 卸載時把視差歸零 + cancel rAF
  useEffect(() => {
    return () => {
      if (scrollRafRef.current != null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
      onScrollParallaxChange?.(0);
    };
  }, [onScrollParallaxChange]);

  return (
    <>
      <div
        className={style.coinCanvasWrapper}
        style={wrapperStyle}
        ref={coinScrollRef}
        onWheel={handleCoinWheel}
        onScroll={handleCoinScroll}
      >
        {/* 切換空間時的小 loading 動畫（蓋在籌碼上方） */}
        {loading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ zIndex: 5, pointerEvents: 'none' }}
          >
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* 真正籌碼畫布 */}
        <div className={style.coinCanvas} ref={coinCanvasRef} style={{ width: canvasWidth }}>
          {cloudCoins.map((coin) => (
            <div
              key={coin.id}
              className={style.coinItem}
              style={{ left: `${coin.x}px`, top: `${coin.y}px` }}
            >
              <button
                type="button"
                data-opener={`article-${coin.id}`}
                onClick={() => handleArticleOpen(coin.id)}
                className="fs-sm-1"
                aria-label={coin.ariaLabel}
              >
                <Coin scale={0.8} color="green">
                  {coin.emojiPic && (
                    <div className={style.emojiPic}>
                      <Image
                        src={coin.emojiPic}
                        alt={coin.emojiName || 'emoji'}
                        width={25}
                        height={25}
                        className="ms-1 align-text-bottom"
                      />
                    </div>
                  )}
                  <Avatar src={coin.avatarSrc} />
                </Coin>
                <span className="text-center fs-sm-2 text-shadow-1">{coin.text}</span>
              </button>
            </div>
          ))}

          {/* CTA 區：二十顆之後「才開始出現」，位置永遠靠右側 */}
          {ctaStartX != null && (
            <div
              className={`${style.coinEndPanel} ${showEndCta ? style.coinEndPanelVisible : ''} ${
                expandEndCta ? style.coinEndPanelExpanded : ''
              }`}
            >
              <div className={style.coinEndPanelInner}>
                <p className="mb-3 text-white fs-sm-1 text-center">
                  這麼多入住回憶，輪到你來寫一篇了！
                </p>
                <div className={`d-flex flex-column flex-md-row gap-2 w-100 ${style.btnGoAR}`}>
                  {addArticleHref &&
                    (token ? (
                      <button
                        type="button"
                        className="btn btn-light btn-sm rounded-pill flex-fill px-3 mb-2 mb-md-0"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          // 1. 決定基礎連結，並判斷是否已有參數
                          const timestamp = Date.now();
                          const freshHref = addArticleHref.includes('?')
                            ? `${addArticleHref}&t=${timestamp}`
                            : `${addArticleHref}?t=${timestamp}`;

                          // 2. 使用原生跳轉強制刷新頁面（取代 router.push）
                          window.location.href = freshHref;
                        }}
                      >
                        寫文入住
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-light btn-sm rounded-pill flex-fill px-3 mb-2 mb-md-0"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          const redirectPath = window.location.pathname + window.location.search;
                          sessionStorage.setItem('womo:auth:redirect', redirectPath);
                          router.push(`/auth/login?redirect=${encodeURIComponent(redirectPath)}`);
                        }}
                      >
                        寫文入住
                      </button>
                    ))}
                  <button
                    type="button"
                    className="btn btn-brand btn-sm rounded-pill flex-fill px-3 bg-brand text-white border-0"
                    onClick={onClickArScan}
                  >
                    AR入住
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 文章 modal */}
      {activeArticleId && (
        <ArticleDetailModal
          open={articleOpen}
          articleId={activeArticleId}
          onClose={handleArticleClose}
        />
      )}
    </>
  );
};

export default CoinCanvas;
